// frontend/src/widgets/WorkflowValidationSummaryWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-15
// Updated:      2026-10-06 16:30 BST
//
// Description:
//   Read-only workflow authoring validation summary.
//
// Change Log (GMT/BST):
//   2026-10-06 16:30 BST - Restored the Validation Summary after the source file had
//                           been overwritten by a duplicate Template List implementation.
//                           Summary is business-facing and explicitly reports lifecycle,
//                           business-task, automation and binding readiness.
//
// Operational Notes:
//   - This widget performs no mutations and no API calls.
//   - Backend publish validation remains authoritative.

import React, { useMemo } from "react";

const WorkflowValidationSummaryWidget = ({ selectedTemplate=null, selectedVersion=null, steps=[], actions=[], bindings=[] }) => {
  const summary = useMemo(() => {
    const errors=[]; const warnings=[]; const info=[];
    if (!selectedTemplate?.id) errors.push("Select a workflow template.");
    if (!selectedVersion?.id) errors.push("Select or create a workflow version.");
    const opened=(steps||[]).filter((s)=>String(s.step_key||"").toLowerCase()==="workflow_opened");
    const completed=(steps||[]).filter((s)=>String(s.step_key||"").toLowerCase()==="workflow_completed");
    if (selectedVersion?.status === "draft") {
      if (opened.length!==1 || completed.length!==1) info.push("System lifecycle markers will be prepared automatically when this draft is published.");
      [...opened,...completed].forEach((s)=>{ if(!s.is_system_locked || s.step_kind!=="system_milestone" || s.completion_mode!=="system_event" || s.assignment_mode!=="unassigned") errors.push("A protected lifecycle marker has an invalid configuration."); });
    }
    const business=(steps||[]).filter((s)=>!["workflow_opened","workflow_completed"].includes(String(s.step_key||"").toLowerCase()));
    if (!business.length) errors.push("Add at least one task before publishing.");
    if (!(bindings||[]).some((b)=>b.is_active)) warnings.push("No active binding exists yet. Publish first, then choose where this workflow applies.");
    if (!(actions||[]).length) info.push("No advanced automation is configured for the selected task. Manual tasks do not require automation.");
    if (selectedVersion?.status === "published") info.push("This version is published and immutable. Create a new draft to change tasks.");
    return {errors,warnings,info};
  },[selectedTemplate,selectedVersion,steps,actions,bindings]);

  const box=(title,items,bg)=> items.length ? <div style={{border:"1px solid #e5e7eb",borderRadius:10,padding:10,background:bg}}><div style={{fontWeight:700,marginBottom:6}}>{title}</div>{items.map((x,i)=><div key={`${title}-${i}`} style={{fontSize:13,marginTop:i?4:0}}>• {x}</div>)}</div> : null;
  const ready=summary.errors.length===0;
  return <div className="flex h-auto min-h-0 flex-col gap-3 overflow-y-auto md:h-full">
    <div style={{border:"1px solid #e5e7eb",borderRadius:10,padding:12,background:ready?"#f0fdf4":"#fff7ed"}}>
      <div style={{fontWeight:800}}>{ready ? "Ready to Publish" : "Not Ready to Publish"}</div>
      <div style={{fontSize:12,color:"#6b7280",marginTop:4}}>{ready ? "The visible authoring checks are satisfied. Backend publish validation remains authoritative." : "Resolve the items below before publishing."}</div>
    </div>
    {box("Needs attention",summary.errors,"#fff7ed")}
    {box("Warnings",summary.warnings,"#fffbeb")}
    {box("Information",summary.info,"#f8fafc")}
  </div>;
};
export default WorkflowValidationSummaryWidget;
